import { TourPrice } from "@/components/currency-estimate";
import { useState, useEffect, useRef, useMemo } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Trans, useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Calendar, Users, MapPin, Plus, Calculator, ChevronDown, X, BookOpen, DollarSign, Clock, Star, MapPinned, Check, ChevronRight, ChevronLeft, CreditCard, Package, Ticket } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { apiRequest } from "@/lib/queryClient";
import { formatEGP } from "@/lib/utils";
import QuoteManager from "@/components/quote-manager";
import CatalogServicePicker, {
  type SelectedCatalogService,
  type CatalogRow,
  tripTypeLabel,
  vehicleLabel,
} from "@/components/catalog-service-picker";
import { GuideSearch } from "@/components/guide-search";
import { AddOnsSearch } from "@/components/addons-search";
import EntranceFeesSearch from "@/components/entrance-fees-search";
import { useTranslatedQuery } from "@/hooks/useTranslatedQuery";
import { useAuth } from "@/hooks/useAuth";
import {
  hasSentConversion,
  hasSentLead,
  markConversionSent,
  markLeadSent,
  trackPurchase,
  trackQualifiedLead,
} from "@/lib/analytics";
import {
  blockersForStep as sharedBlockersForStep,
  type QuoteState,
} from "@shared/quote-validation";

interface CityService {
  dayNumber: number;
  cityId: number;
  cityName: string;
  date: string;
  travelers: number;
  // Phase C: catalog selections replace numeric route IDs. The server
  // turns each entry into a frozen line item via /api/quotes
  // (serviceSlugs key). The legacy `selectedRoutes` shape is gone from
  // the planner; /api/routes still serves data but the planner no
  // longer reads it.
  selectedServices: SelectedCatalogService[];
  selectedGuide?: {
    language: string;
    duration: number;
  };
  attractions: string;
  selectedAttractions: string[];
  selectedEntranceFees?: string[];
  selectedAddOns: Array<{
    id: number;
    name: string;
    quantity: number;
    unitType?: string;
    price?: string;
  }>;
}

interface AddOn {
  id: number;
  name: string;
  description: string;
  price: string;
  unitType: string;
  cityId: number | null;
  category: string;
  image: string | null;
  isActive: boolean;
}

// Progress Indicator Component
function StepProgress({ currentStep, steps, onStepClick }: { 
  currentStep: number; 
  steps: Array<{ number: number; title: string; description: string }>; 
  onStepClick?: (step: number) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="mb-8">
      <nav aria-label={t('quoteBuilder.progress.ariaLabel')} className="flex items-center justify-between max-w-4xl mx-auto">
        {steps.map((step, index) => (
          <div key={step.number} className="flex items-center flex-1">
            <div className="flex flex-col items-center flex-1">
              <button
                onClick={() => onStepClick?.(step.number)}
                disabled={step.number > currentStep + 1}
                // The visible label is a bare digit (or a checkmark once done),
                // which tells a screen-reader user nothing about the step.
                aria-label={t(
                step.number < currentStep
                  ? 'quoteBuilder.progress.stepDone'
                  : 'quoteBuilder.progress.step',
                { number: step.number, title: step.title },
              )}
                aria-current={step.number === currentStep ? 'step' : undefined}
                className={`
                  w-11 h-11 rounded-full flex items-center justify-center font-semibold transition-all
                  ${step.number < currentStep 
                    ? 'bg-primary text-white' 
                    : step.number === currentStep 
                    ? 'bg-primary text-white ring-4 ring-primary/20' 
                    : 'bg-gray-200 text-gray-500'}
                  ${step.number <= currentStep ? 'cursor-pointer hover:scale-110' : 'cursor-not-allowed'}
                `}
              >
                {step.number < currentStep ? <Check className="w-5 h-5" /> : step.number}
              </button>
              <div className="mt-2 text-center hidden sm:block">
                <div className={`text-xs font-medium ${step.number === currentStep ? 'text-primary' : 'text-gray-600'}`}>
                  {step.title}
                </div>
                <div className="text-xs text-muted-foreground hidden lg:block">{step.description}</div>
              </div>
            </div>
            {index < steps.length - 1 && (
              <div aria-hidden="true" className={`flex-1 h-1 mx-2 rounded transition-all ${step.number < currentStep ? 'bg-primary' : 'bg-gray-200'}`} />
            )}
          </div>
        ))}
      </nav>
    </div>
  );
}

/**
 * Inline validation message rendered directly beneath the field it belongs to.
 * role="alert" so screen readers announce it when it appears after a blocked
 * submit; the id lets the field point at it via aria-describedby.
 */
function FieldError({ id, message }: { id: string; message?: string }) {
  if (!message) return null;
  return (
    <p id={id} role="alert" className="mt-1.5 text-xs font-medium text-red-600">
      {message}
    </p>
  );
}

// Sticky live-price panel shown in the right pane of the planner steps.
// Reads the same `totalPricing` the preview produces; full per-city totals
// (server breakdown is full, not per-person) so the rows sum to the total.
function LivePriceSummary({
  totalPricing,
  onContinue,
  ctaLabel,
  ctaDisabled,
}: {
  totalPricing: any;
  onContinue?: () => void;
  ctaLabel?: string;
  ctaDisabled?: boolean;
}) {
  const { t } = useTranslation();
  const hasPricing = totalPricing && totalPricing.totalAmount > 0;
  return (
    <div className="lg:sticky lg:top-4 h-fit">
      <Card className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <DollarSign className="w-4 h-4 text-primary" />
          <h3 className="font-semibold">{t('quoteBuilder.livePrice.title')}</h3>
        </div>
        {hasPricing ? (
          <>
            <div className="space-y-1.5 text-sm">
              {(totalPricing.breakdown ?? []).map((b: any, i: number) => (
                <div key={i} className="flex justify-between gap-3">
                  <span className="text-muted-foreground truncate">{b.city}</span>
                  <span className="whitespace-nowrap"><TourPrice amount={b.total} /></span>
                </div>
              ))}
            </div>
            <div className="flex justify-between items-baseline mt-3 pt-3 border-t">
              <span className="text-sm text-muted-foreground">
                {t('quoteBuilder.livePrice.totalWithTravelers', { count: totalPricing.travelers })}
              </span>
              <span className="text-xl font-bold text-primary"><TourPrice amount={totalPricing.totalAmount} showNote /></span>
            </div>
            <p className="text-xs text-muted-foreground">{t('quoteBuilder.livePrice.perPerson', { price: formatEGP(totalPricing.perPersonAmount) })}</p>
          </>
        ) : (
          <p className="text-sm text-muted-foreground">{t('quoteBuilder.livePrice.empty')}</p>
        )}
        {onContinue && (
          <Button
            onClick={onContinue}
            disabled={ctaDisabled}
            className="w-full mt-4 bg-gradient-to-r from-primary to-blue-600"
          >
            {ctaLabel ?? t('quoteBuilder.livePrice.continue')}
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        )}
        <p className="text-xs text-center text-muted-foreground mt-2">
          {t('quoteBuilder.livePrice.footnote')}
        </p>
      </Card>
    </div>
  );
}

export default function MultiCityPricingTool() {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const hasLocalSavedQuote = typeof window !== "undefined"
    && !!localStorage.getItem("saved_quote_id");

  // Saved Quotes tab is only meaningful for users who already have one.
  // Logged-in users may have server-side quotes; anonymous users may have a
  // saved-quote ID in localStorage from a previous session.
  const { data: serverQuotes = [] } = useQuery<any[]>({
    queryKey: ["/api/quotes"],
    enabled: isAuthenticated,
  });
  const showSavedQuotesTab = (isAuthenticated && serverQuotes.length > 0) || hasLocalSavedQuote;
  
  // Step-based navigation
  const [currentStep, setCurrentStep] = useState(1);
  const stepContentRef = useRef<HTMLDivElement>(null);
  
  // Core state
  const [cityServices, setCityServices] = useState<CityService[]>([]);
  const [totalPricing, setTotalPricing] = useState<any>(null);
  const [travelDate, setTravelDate] = useState<string | null>('');
  const [justExploring, setJustExploring] = useState<boolean>(false);
  const [tripDuration, setTripDuration] = useState<string>('');
  // Field-keyed validation messages for the step the user is trying to leave.
  const [stepErrors, setStepErrors] = useState<Record<string, string>>({});
  // Day the user asked to edit from the review step, so we can scroll to it and
  // mark it. Cleared once the highlight has served its purpose.
  const [focusedDay, setFocusedDay] = useState<number | null>(null);
  const [step1DestinationId, setStep1DestinationId] = useState<string>('');
  const [globalTravelers, setGlobalTravelers] = useState<number>(1);
  
  // Checkout form state
  const [checkoutData, setCheckoutData] = useState({
    name: '',
    email: '',
    phone: '',
    nationality: '',
    accommodation: '',
    specialRequests: '',
    termsAccepted: false,
    bookingPolicyAccepted: false,
    updatesConsent: false
  });
  

  // Fetch available cities from the database with translation
  const { data: allCities = [] } = useTranslatedQuery<{id: number, name: string, description?: string}[]>("/api/cities");

  // Filter out cities without services and sort alphabetically
  const cities = useMemo(() => {
    const excludedCities = [
      'asyut', 'beni suef', 'esna', 'edfu', 'kom ombo', 'qena', 
      'quseir', 'ain sokhna', 'dakhla oasis', 'kharga oasis', 'ras sudr'
    ];
    
    return allCities
      .filter((city: any) => !excludedCities.includes(city.name.toLowerCase().trim()))
      .sort((a: any, b: any) => a.name.localeCompare(b.name));
  }, [allCities]);

  // Fetch available languages
  const { data: languages = [] } = useQuery<string[]>({
    queryKey: ["/api/pricing/languages"],
  });

  // Fetch available add-ons
  const { data: addOns = [] } = useQuery<AddOn[]>({
    queryKey: ["/api/addons"],
  });




  const { data: entranceFees = [] } = useTranslatedQuery<any[]>("/api/entrance-fees");

  // Calculate pricing mutation
  const pricingMutation = useMutation({
    mutationFn: async (services: CityService[]) => {
      const response = await apiRequest("POST", "/api/pricing/calculate", {
        cityServices: services
      });
      return response.json();
    },
    onSuccess: (data) => {
      setTotalPricing(data);
    }
  });

  // Remove auto-initialization - let user add first city manually

  // Recalculate pricing when city services change
  useEffect(() => {
    if (cityServices.length > 0) {
      pricingMutation.mutate(cityServices);
    }
  }, [cityServices]);

  // Phase C deep-link: ?service=<slug>&city=<city> on landing.
  // The "Book this" buttons on the six service-area pages link here.
  // Behavior: fetch the service, look up the cities-table id by name,
  // pre-populate cityServices + selectedServices, jump to the right
  // step based on the row's category. Runs once when cities have
  // loaded; bails silently if anything is missing.
  const [deepLinkApplied, setDeepLinkApplied] = useState(false);
  useEffect(() => {
    if (deepLinkApplied) return;
    if (typeof window === "undefined") return;
    if (!cities || cities.length === 0) return;

    const params = new URLSearchParams(window.location.search);
    const slug = params.get("service");
    const cityParam = params.get("city");
    if (!slug || !cityParam) return;

    setDeepLinkApplied(true);

    (async () => {
      try {
        const res = await fetch(`/api/services/${encodeURIComponent(slug)}`);
        if (!res.ok) return;
        const row: CatalogRow = await res.json();

        // Map URL city slug to cities-table row by case-insensitive
        // name match. The catalog row's city column is the canonical
        // display form ("Cairo", "Marsa Alam") so we match against
        // both the URL param and the row's city.
        const wanted = cityParam.toLowerCase().replace(/-/g, " ");
        const wantedFromRow = (row.city || "").toLowerCase();
        const matched = cities.find(
          (c: any) =>
            c.name.toLowerCase() === wanted ||
            c.name.toLowerCase() === wantedFromRow,
        );
        if (!matched) return;

        // Pick a default vehicle + the row's single trip type.
        const keys = Object.keys(row.vehicle_prices ?? {});
        if (keys.length === 0) return;
        let tripType: string | null = null;
        for (const k of keys) {
          for (const v of ["sedan", "minivan", "van"]) {
            if (k.startsWith(`${v}_`)) {
              tripType = k.slice(v.length + 1);
              break;
            }
          }
          if (tripType) break;
        }
        if (!tripType) return;
        const defaultVehicle: SelectedCatalogService["vehicleSlug"] =
          `sedan_${tripType}` in row.vehicle_prices
            ? "sedan"
            : `minivan_${tripType}` in row.vehicle_prices
              ? "minivan"
              : "van";

        const selectedService: SelectedCatalogService = {
          slug: row.slug,
          vehicleSlug: defaultVehicle,
          tripType,
          name: row.name,
          price: row.vehicle_prices[`${defaultVehicle}_${tripType}`],
        };

        const today = new Date().toISOString().split("T")[0];
        const newCityService: CityService = {
          dayNumber: 1,
          cityId: matched.id,
          cityName: matched.name,
          date: today,
          travelers: 1,
          selectedServices: [selectedService],
          attractions: "",
          selectedAttractions: [],
          selectedAddOns: [],
        };

        // Step-1 fields the canProceedToStep guards check.
        setStep1DestinationId(String(matched.id));
        setGlobalTravelers(1);
        setTravelDate(today);
        setCityServices([newCityService]);

        // Everything is built in step 2 now (one-window itinerary), so all
        // deep-links land there.
        setCurrentStep(2);

        // Scroll the planner into view.
        setTimeout(() => {
          stepContentRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }, 200);
      } catch {
        // Swallow — deep-link is best-effort.
      }
    })();
  }, [cities, deepLinkApplied]);




  // (removed dead handleContinueBooking — an unused /api/quotes checkout that
  // froze only transfers; the live step-5 checkout below now freezes the full
  // itinerary via cityServices.)


  const loadQuoteData = (quoteData: any) => {
    if (quoteData.cityServices) {
      setCityServices(quoteData.cityServices);
    }
    if (quoteData.travelDate) {
      setTravelDate(quoteData.travelDate);
    }
    if (quoteData.totalTravelers || quoteData.travelers) {
      setGlobalTravelers(quoteData.totalTravelers || quoteData.travelers);
    }
  };

  const getCurrentQuoteData = () => {
    return {
      cityServices,
      travelDate,
      totalTravelers: globalTravelers,
      travelers: globalTravelers,
      totalPricing
    };
  };









  // Step navigation helpers
  const steps = [
    { number: 1, title: t('quoteBuilder.steps.overviewTitle'), description: t('quoteBuilder.steps.overviewDescription') },
    { number: 2, title: t('quoteBuilder.steps.itineraryTitle'), description: t('quoteBuilder.steps.itineraryDescription') },
    { number: 3, title: t('quoteBuilder.steps.reviewTitle'), description: t('quoteBuilder.steps.reviewDescription') },
    { number: 4, title: t('quoteBuilder.steps.checkoutTitle'), description: t('quoteBuilder.steps.checkoutDescription') }
  ];

  // Rules live in @shared/quote-validation so they can be unit-tested without
  // a browser — see shared/quote-validation.test.ts.
  const validationState: QuoteState = {
    destinationId: step1DestinationId,
    tripDuration,
    travelers: globalTravelers,
    travelDate,
    justExploring,
    days: cityServices,
    totalAmount: totalPricing?.totalAmount ?? 0,
  };

  // The shared rules return reason codes; the words live in the locale files.
  const blockersForStep = (step: number): Record<string, string> => {
    const codes = sharedBlockersForStep(step, validationState);
    return Object.fromEntries(
      Object.entries(codes).map(([field, code]) => [field, t(`quoteBuilder.blockers.${code}`)]),
    );
  };

  const goToNextStep = () => {
    if (currentStep >= 4) return;
    const next = currentStep + 1;
    const blockers = blockersForStep(next);
    if (Object.keys(blockers).length > 0) {
      // Previously this silently did nothing, so the CTA read as broken.
      setStepErrors(blockers);
      return;
    }
    setStepErrors({});
    setCurrentStep(next);
    // Scroll to step content area, not top of page
    setTimeout(() => {
      stepContentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  /**
   * "Edit Day N" from the review step.
   *
   * Previously this was a bare setCurrentStep(2): the itinerary data survived
   * (nothing clears cityServices), but the user landed at the top of step 2
   * with no indication of which of their days they had asked to edit — on a
   * five-day trip that means hunting for it. Scrolling to the card and marking
   * it briefly is what makes the return *legible*, which is the part the audit
   * was actually pointing at.
   */
  const editDay = (dayNumber: number) => {
    setStepErrors({});
    setFocusedDay(dayNumber);
    setCurrentStep(2);
    // After the step-2 tree has mounted.
    setTimeout(() => {
      document
        .getElementById(`day-card-${dayNumber}`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 100);
  };

  // Drop the highlight once it has done its job, so it doesn't linger as
  // permanent decoration on a card the user has moved on from.
  useEffect(() => {
    if (focusedDay === null) return;
    const id = setTimeout(() => setFocusedDay(null), 2500);
    return () => clearTimeout(id);
  }, [focusedDay]);

  const goToPreviousStep = () => {
    if (currentStep > 1) {
      setStepErrors({});
      setCurrentStep(currentStep - 1);
      // Scroll to step content area, not top of page
      setTimeout(() => {
        stepContentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  };

  return (
    <div id="quote-builder" className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
      {/* Section heading lives in the card's CardTitle below (with the
          calculator icon + subline) — no separate outer <h2>, which used to
          duplicate "Design Your Egypt Adventure". */}
      <Tabs defaultValue="pricing" className="w-full">
        {showSavedQuotesTab && (
          <TabsList className="grid w-full grid-cols-2 mb-6 mx-auto max-w-md">
            <TabsTrigger value="pricing" className="flex items-center gap-2">
              <Calculator className="w-4 h-4" />
              <span className="hidden sm:inline">{t('quoteBuilder.tabs.build')}</span>
              <span className="sm:hidden">{t('quoteBuilder.tabs.buildShort')}</span>
            </TabsTrigger>
            <TabsTrigger value="saved" className="flex items-center gap-2">
              <BookOpen className="w-4 h-4" />
              <span className="hidden sm:inline">{t('quoteBuilder.tabs.saved')}</span>
              <span className="sm:hidden">{t('quoteBuilder.tabs.savedShort')}</span>
            </TabsTrigger>
          </TabsList>
        )}

        <TabsContent value="pricing">
          <Card>
            <CardHeader className="text-center pb-4">
              <CardTitle className="flex items-center justify-center gap-2 text-xl sm:text-2xl">
                <Calculator className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
                <span>{t('quoteBuilder.header.title')}</span>
              </CardTitle>
              <p className="text-muted-foreground text-sm">
                {t('quoteBuilder.header.subtitle')}
              </p>
            </CardHeader>
            
            <CardContent>
              {/* Progress Indicator */}
              <StepProgress 
                currentStep={currentStep} 
                steps={steps}
                onStepClick={(step) => {
                  if (step > currentStep) return;
                  setStepErrors({});
                  setCurrentStep(step);
                  // goToNextStep/goToPreviousStep both scroll; jumping via the
                  // progress bar did not, so it could leave you mid-page.
                  setTimeout(() => {
                    stepContentRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
                  }, 100);
                }}
              />

              {/* Step Content Container - Scroll Target */}
              <div ref={stepContentRef}>
                {/* STEP 1: Trip Overview */}
                {currentStep === 1 && (
                <div className="animate-in fade-in duration-500 px-4 py-6 sm:py-10">
                  <div className="max-w-3xl mx-auto bg-white border border-gray-200 rounded-2xl shadow-sm p-6 sm:p-10">
                    {/* Header */}
                    <div className="text-center mb-7 sm:mb-8">
                      <h3 className="text-2xl sm:text-3xl font-bold mb-2 bg-gradient-to-r from-teal-600 to-blue-600 bg-clip-text text-transparent">
                        {t('quoteBuilder.step1.title')}
                      </h3>
                      <p className="text-sm text-muted-foreground">{t('quoteBuilder.step1.subtitle')}</p>
                    </div>

                    {/* Compact 2-up field grid — stacks to one column on mobile */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                      {/* Destination */}
                      <div>
                        <Label htmlFor="step1-destination" className="text-xs font-semibold mb-1.5 flex items-center gap-1.5 text-gray-600">
                          <MapPin className="w-4 h-4 text-teal-600" />
                          {t('quoteBuilder.step1.destinationLabel')}
                        </Label>
                        <Select
                          value={step1DestinationId}
                          onValueChange={(value) => {
                            setStep1DestinationId(value);
                            setStepErrors(prev => ({ ...prev, destination: '' }));
                            const selectedCity = cities.find((c: any) => c.id === parseInt(value));
                            if (!selectedCity) return;
                            // Seed first day if no city selected yet
                            setCityServices(prev => {
                              if (prev.length === 0) {
                                return [{
                                  dayNumber: 1,
                                  cityId: selectedCity.id,
                                  cityName: selectedCity.name,
                                  date: travelDate || '',
                                  travelers: globalTravelers,
                                  selectedServices: [],
                                  attractions: '',
                                  selectedAttractions: [],
                                  selectedAddOns: []
                                }];
                              }
                              // Replace day 1 with new selection
                              const updated = [...prev];
                              updated[0] = { ...updated[0], cityId: selectedCity.id, cityName: selectedCity.name };
                              return updated;
                            });
                          }}
                        >
                          <SelectTrigger
                            id="step1-destination"
                            aria-label={t('quoteBuilder.step1.destinationLabel')}
                            aria-invalid={!!stepErrors.destination}
                            aria-describedby={stepErrors.destination ? 'step1-destination-error' : undefined}
                            className={`w-full h-11 rounded-lg focus:border-teal-600 focus:ring-2 focus:ring-teal-100 transition-all duration-200 ${stepErrors.destination ? 'border-red-500' : 'border-gray-300'}`}
                          >
                            <SelectValue placeholder={t('quoteBuilder.step1.destinationPlaceholder')} />
                          </SelectTrigger>
                          <SelectContent>
                            {cities?.map((city: any) => (
                              <SelectItem key={city.id} value={city.id.toString()}>
                                <div className="flex items-center gap-2">
                                  <MapPin className="w-4 h-4 text-primary" />
                                  {city.name}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FieldError id="step1-destination-error" message={stepErrors.destination} />
                      </div>

                      {/* How many days */}
                      <div>
                        <Label htmlFor="trip-duration" className="text-xs font-semibold mb-1.5 flex items-center gap-1.5 text-gray-600">
                          <Clock className="w-4 h-4 text-teal-600" />
                          {t('quoteBuilder.step1.daysLabel')}
                        </Label>
                        <Select
                          value={tripDuration}
                          onValueChange={(value) => {
                            setTripDuration(value);
                            setStepErrors(prev => ({ ...prev, duration: '' }));
                          }}
                        >
                          <SelectTrigger
                            id="trip-duration"
                            aria-label={t('quoteBuilder.step1.daysLabel')}
                            aria-invalid={!!stepErrors.duration}
                            aria-describedby={stepErrors.duration ? 'trip-duration-error' : undefined}
                            className={`w-full h-11 rounded-lg focus:border-teal-600 focus:ring-2 focus:ring-teal-100 transition-all duration-200 ${stepErrors.duration ? 'border-red-500' : 'border-gray-300'}`}
                          >
                            <SelectValue placeholder={t('quoteBuilder.step1.daysPlaceholder')} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="1-2">{t('quoteBuilder.step1.duration1to2')}</SelectItem>
                            <SelectItem value="3-4">{t('quoteBuilder.step1.duration3to4')}</SelectItem>
                            <SelectItem value="5-7">{t('quoteBuilder.step1.duration5to7')}</SelectItem>
                            <SelectItem value="8-14">{t('quoteBuilder.step1.duration8to14')}</SelectItem>
                            <SelectItem value="15+">{t('quoteBuilder.step1.duration15plus')}</SelectItem>
                            <SelectItem value="exploring">{t('quoteBuilder.step1.durationExploring')}</SelectItem>
                          </SelectContent>
                        </Select>
                        <FieldError id="trip-duration-error" message={stepErrors.duration} />
                      </div>

                      {/* Travelers */}
                      <div>
                        <Label htmlFor="total-travelers" className="text-xs font-semibold mb-1.5 flex items-center gap-1.5 text-gray-600">
                          <Users className="w-4 h-4 text-teal-600" />
                          {t('quoteBuilder.step1.travelersLabel')}
                        </Label>
                        <Select
                          value={globalTravelers.toString()}
                          onValueChange={(value) => {
                            const newTravelers = parseInt(value);
                            setGlobalTravelers(newTravelers);
                            setCityServices(prev => prev.map(city => ({ ...city, travelers: newTravelers })));
                          }}
                        >
                          <SelectTrigger
                            id="total-travelers"
                            aria-label={t('quoteBuilder.step1.travelersLabel')}
                            className="w-full h-11 rounded-lg border-gray-300 focus:border-teal-600 focus:ring-2 focus:ring-teal-100 transition-all duration-200"
                          >
                            <SelectValue placeholder={t('quoteBuilder.step1.travelersPlaceholder')} />
                          </SelectTrigger>
                          <SelectContent>
                            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                              <SelectItem key={num} value={num.toString()}>{t('quoteBuilder.step1.travelerCount', { count: num })}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Travel date + "not sure yet" toggle, sharing one cell */}
                      <div>
                        <div className="flex items-center justify-between mb-1.5">
                          <Label htmlFor="travel-date" className="text-xs font-semibold flex items-center gap-1.5 text-gray-600">
                            <Calendar className="w-4 h-4 text-teal-600" />
                            {t('quoteBuilder.step1.whenLabel')}
                          </Label>
                          {/* The Radix checkbox renders 14x14. The label is the
                              real hit area, and at 89x16 it was far under the
                              44px minimum — on a control in the quote builder's
                              critical path. Padding lifts the combined target
                              without moving the checkbox visually. */}
                          <label
                            htmlFor="just-exploring"
                            className="flex min-h-11 items-center gap-1.5 px-2 -mr-2 text-xs text-gray-500 cursor-pointer select-none"
                          >
                            <Checkbox
                              id="just-exploring"
                              // Radix renders a <button role="checkbox">, whose only
                              // text child is the label's — which the wrapping <label>
                              // does not supply to it. Name it explicitly.
                              aria-label={t('quoteBuilder.step1.notSureYetAria')}
                              className="h-3.5 w-3.5"
                              checked={justExploring}
                              onCheckedChange={(checked) => {
                                const isChecked = checked === true;
                                setJustExploring(isChecked);
                                if (isChecked) {
                                  setTravelDate(null);
                                  setCityServices(prev => prev.map(city => ({ ...city, date: '' })));
                                }
                                if (isChecked) setStepErrors(prev => ({ ...prev, date: '' }));
                              }}
                            />
                            {t('quoteBuilder.step1.notSureYet')}
                          </label>
                        </div>
                        <Input
                          id="travel-date"
                          type="date"
                          value={travelDate ?? ''}
                          disabled={justExploring}
                          aria-invalid={!!stepErrors.date}
                          aria-describedby={stepErrors.date ? 'travel-date-error' : undefined}
                          onChange={(e) => {
                            setTravelDate(e.target.value);
                            setStepErrors(prev => ({ ...prev, date: '' }));
                            setCityServices(prev => prev.map(city => ({ ...city, date: e.target.value })));
                          }}
                          className={`w-full h-11 rounded-lg focus:border-teal-600 focus:ring-2 focus:ring-teal-100 transition-all duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed ${stepErrors.date ? 'border-red-500' : 'border-gray-300'}`}
                        />
                        <FieldError id="travel-date-error" message={stepErrors.date} />
                      </div>
                    </div>

                    {/* Trust row */}
                    <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 mt-7 text-sm text-gray-600">
                      <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-teal-600 shrink-0" /> {t('quoteBuilder.step1.trustVehicle')}</span>
                      <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-teal-600 shrink-0" /> {t('quoteBuilder.step1.trustPricing')}</span>
                      <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-teal-600 shrink-0" /> {t('quoteBuilder.step1.trustInstant')}</span>
                    </div>

                    {/* CTA */}
                    <div className="flex justify-center mt-8">
                      {/* Deliberately not disabled: a dead button explains nothing.
                          goToNextStep surfaces per-field errors instead. */}
                      <Button
                        onClick={goToNextStep}
                        size="lg"
                        className="bg-gradient-to-r from-teal-600 to-teal-700 hover:from-teal-700 hover:to-teal-800 text-white shadow-lg hover:shadow-xl transition-all duration-200 w-full sm:w-auto px-10 py-6 text-base font-semibold rounded-xl"
                      >
                        {/* Step 2 is where the price forms — promising a price
                            on this click was a small untruth. */}
                        {t('quoteBuilder.step1.cta')}
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* STEP 2: Destinations Selection */}
              {currentStep === 2 && (
                <div className="animate-in fade-in duration-300 space-y-6">
                  {/* Header */}
                  <div className="text-center lg:text-left">
                    <h2 className="text-2xl font-bold mb-2">{t('quoteBuilder.step2.title')}</h2>
                    <p className="text-muted-foreground">{t('quoteBuilder.step2.subtitle')}</p>
                  </div>

                  {/* First-destination selector (empty state) */}
                  {cityServices.length === 0 && (
                    <div className="p-6 bg-gradient-to-br from-blue-50 to-purple-50 border-2 border-dashed border-blue-200 rounded-lg">
                      <div className="text-center mb-6">
                        <MapPin className="w-12 h-12 mx-auto mb-3 text-blue-600" />
                        <h3 className="text-lg font-semibold mb-2">{t('quoteBuilder.step2.firstDestinationTitle')}</h3>
                        <p className="text-sm text-muted-foreground">{t('quoteBuilder.step2.firstDestinationSubtitle')}</p>
                      </div>
                      <div className="max-w-md mx-auto">
                        <Label className="text-sm font-medium mb-2 block">{t('quoteBuilder.step2.selectDestinationLabel')}</Label>
                        <Select
                          onValueChange={(value) => {
                            if (!cities || cities.length === 0) return;
                            const selectedCity = cities.find((c: any) => c.id === parseInt(value));
                            if (!selectedCity) return;
                            const newCity: CityService = {
                              dayNumber: 1,
                              cityId: selectedCity.id,
                              cityName: selectedCity.name,
                              date: travelDate ?? '',
                              travelers: globalTravelers,
                              selectedServices: [],
                              attractions: '',
                              selectedAttractions: [],
                              selectedEntranceFees: [],
                              selectedAddOns: []
                            };
                            setCityServices([newCity]);
                          }}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder={t('quoteBuilder.step2.chooseCityPlaceholder')} />
                          </SelectTrigger>
                          <SelectContent>
                            {cities?.map((city: any) => (
                              <SelectItem key={city.id} value={city.id.toString()}>
                                <div className="flex items-center gap-2">
                                  <MapPin className="w-4 h-4 text-primary" />
                                  {city.name}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}

                  {/* Day strips — one row per day, columns left→right */}
                  {cityServices.length > 0 && (
                    <div className="space-y-4">
                      {cityServices.map((city, index) => {
                        const dayTotal = totalPricing?.breakdown?.[index]?.total;
                        const tCount = city.selectedServices?.length || 0;
                        const transferSummary = tCount === 0
                          ? t('quoteBuilder.step2.selectTransfer')
                          : tCount === 1
                            ? (city.selectedServices[0]?.name || t('quoteBuilder.step2.transferCount', { count: 1 }))
                            : t('quoteBuilder.step2.transferCount', { count: tCount });
                        return (
                          <Card
                            key={index}
                            id={`day-card-${city.dayNumber}`}
                            className={`p-4 transition-shadow duration-500 ${
                              focusedDay === city.dayNumber
                                ? "ring-2 ring-teal-500 ring-offset-2"
                                : ""
                            }`}
                          >
                            {/* strip header */}
                            <div className="flex items-center gap-2 mb-3 pb-3 border-b">
                              <Badge className="bg-primary">{t('quoteBuilder.step2.day', { number: city.dayNumber })}</Badge>
                              <div className="flex items-center gap-1 font-semibold min-w-0">
                                <MapPin className="w-4 h-4 text-primary shrink-0" />
                                <span className="truncate">{city.cityName}</span>
                              </div>
                              {dayTotal !== undefined && dayTotal > 0 && (
                                <span className="ml-auto text-sm font-semibold text-primary"><TourPrice amount={dayTotal} /></span>
                              )}
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setCityServices(prev => prev.filter((_, i) => i !== index).map((c, i) => ({ ...c, dayNumber: i + 1 })))}
                                className={`${dayTotal ? '' : 'ml-auto'} h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50`}
                                aria-label={t('quoteBuilder.step2.removeDay', { number: city.dayNumber })}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>

                            {/* 5 columns: Date · Transfer · Guide · Entrance fees · Add-ons */}
                            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
                              {/* Date */}
                              <div className="space-y-1.5">
                                <Label className="text-xs text-muted-foreground flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{t('quoteBuilder.step2.dateLabel')}</Label>
                                <Input
                                  type="date"
                                  value={city.date || ''}
                                  onChange={(e) => setCityServices(prev => prev.map((c, i) => i === index ? { ...c, date: e.target.value } : c))}
                                  className="h-9 text-sm"
                                />
                              </div>

                              {/* Transfer (inline picker wrapped in a popover so it fits a column) */}
                              <div className="space-y-1.5">
                                <Label className="text-xs text-muted-foreground flex items-center gap-1"><MapPinned className="w-3.5 h-3.5" />{t('quoteBuilder.step2.transferLabel')}</Label>
                                <Popover>
                                  <PopoverTrigger asChild>
                                    <Button variant="outline" className="w-full justify-between h-9 text-sm font-normal">
                                      <span className="truncate">{transferSummary}</span>
                                      <ChevronDown className="h-4 w-4 opacity-50 shrink-0" />
                                    </Button>
                                  </PopoverTrigger>
                                  <PopoverContent className="w-96 p-3" align="start">
                                    <CatalogServicePicker
                                      city={city.cityName}
                                      categories={["airport_transfer","intercity_transfer","local_transfer","tour_transfer"]}
                                      selected={city.selectedServices}
                                      travelers={city.travelers || globalTravelers}
                                      onChange={(next) => setCityServices(prev => prev.map((c, i) => i === index ? { ...c, selectedServices: next } : c))}
                                    />
                                  </PopoverContent>
                                </Popover>
                              </div>

                              {/* Guide */}
                              <div className="space-y-1.5">
                                <Label className="text-xs text-muted-foreground flex items-center gap-1"><Users className="w-3.5 h-3.5" />{t('quoteBuilder.step2.guideLabel')}</Label>
                                <GuideSearch
                                  languages={languages || []}
                                  cityId={city.cityId}
                                  cityName={city.cityName}
                                  selectedGuide={city.selectedGuide}
                                  onGuideChange={(guide) => setCityServices(prev => prev.map((c, i) => i === index ? { ...c, selectedGuide: guide } : c))}
                                />
                              </div>

                              {/* Entrance fees */}
                              <div className="space-y-1.5">
                                <Label className="text-xs text-muted-foreground flex items-center gap-1"><Ticket className="w-3.5 h-3.5" />{t('quoteBuilder.step2.entranceFeesLabel')}</Label>
                                <EntranceFeesSearch
                                  entranceFees={entranceFees || []}
                                  cityName={city.cityName}
                                  selectedEntranceFees={city.selectedEntranceFees || []}
                                  onEntranceFeesChange={(slugs) => setCityServices(prev => prev.map((c, i) => i === index ? { ...c, selectedEntranceFees: slugs } : c))}
                                />
                              </div>

                              {/* Add-ons */}
                              <div className="space-y-1.5">
                                <Label className="text-xs text-muted-foreground flex items-center gap-1"><Package className="w-3.5 h-3.5" />{t('quoteBuilder.step2.addOnsLabel')}</Label>
                                <AddOnsSearch
                                  addOns={addOns || []}
                                  selectedAddOns={city.selectedAddOns || []}
                                  onAddOnsChange={(next) => setCityServices(prev => prev.map((c, i) => i === index ? { ...c, selectedAddOns: next } : c))}
                                  cityId={city.cityId}
                                  cityName={city.cityName}
                                />
                              </div>
                            </div>
                          </Card>
                        );
                      })}
                    </div>
                  )}

                  {/* Add day */}
                  {cityServices.length > 0 && (
                    <div className="p-4 bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg">
                      <div className="flex items-center gap-4">
                        <Label className="text-sm font-medium whitespace-nowrap">
                          <Plus className="w-4 h-4 inline mr-2" />
                          {t('quoteBuilder.step2.addDay', { number: cityServices.length + 1 })}
                        </Label>
                        <Select
                          onValueChange={(value) => {
                            if (!cities || cities.length === 0) return;
                            const selectedCity = cities.find((c: any) => c.id === parseInt(value));
                            if (!selectedCity) return;
                            const nextDayNumber = cityServices.length + 1;
                            const nextDate = travelDate ?
                              new Date(new Date(travelDate).getTime() + (nextDayNumber - 1) * 24 * 60 * 60 * 1000).toISOString().split('T')[0] : '';
                            const newCity: CityService = {
                              dayNumber: nextDayNumber,
                              cityId: selectedCity.id,
                              cityName: selectedCity.name,
                              date: nextDate,
                              travelers: globalTravelers,
                              selectedServices: [],
                              attractions: '',
                              selectedAttractions: [],
                              selectedEntranceFees: [],
                              selectedAddOns: []
                            };
                            setCityServices(prev => [...prev, newCity]);
                          }}
                        >
                          <SelectTrigger className="flex-1">
                            <SelectValue placeholder={t('quoteBuilder.step2.nextDayPlaceholder')} />
                          </SelectTrigger>
                          <SelectContent>
                            {cities?.map((city: any) => (
                              <SelectItem key={city.id} value={city.id.toString()}>
                                <div className="flex items-center gap-2">
                                  <MapPin className="w-4 h-4 text-primary" />
                                  {city.name}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}

                  {/* Bottom total bar */}
                  {cityServices.length > 0 && totalPricing && totalPricing.totalAmount > 0 && (
                    <div className="flex items-center justify-between gap-4 p-4 bg-primary/5 rounded-lg border">
                      <div>
                        <div className="text-sm text-muted-foreground">{t('quoteBuilder.livePrice.totalWithTravelers', { count: totalPricing.travelers })}</div>
                        <div className="text-xs text-muted-foreground">{t('quoteBuilder.livePrice.perPerson', { price: formatEGP(totalPricing.perPersonAmount) })}</div>
                      </div>
                      <div className="text-2xl font-bold text-primary"><TourPrice amount={totalPricing.totalAmount} /></div>
                    </div>
                  )}

                  {/* Navigation */}
                  <div className="pt-6 border-t">
                    <div className="flex flex-col-reverse sm:flex-row justify-center sm:justify-between gap-3">
                      <Button variant="outline" onClick={goToPreviousStep} size="lg" className="w-full sm:w-auto">
                        <ChevronLeft className="w-4 h-4 mr-2" />
                        {t('quoteBuilder.step2.back')}
                      </Button>
                      <Button onClick={goToNextStep} size="lg" className="bg-gradient-to-r from-primary to-blue-600 w-full sm:w-auto">
                        {t('quoteBuilder.step2.continue')}
                        <ChevronRight className="w-4 h-4 ml-2" />
                      </Button>
                    </div>
                    <FieldError id="step2-itinerary-error" message={stepErrors.itinerary} />
                  </div>
                </div>
              )}

              {/* STEP 3: Review & Pricing */}
              {currentStep === 3 && (
                <div className="animate-in fade-in duration-300">
                  <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
                  <div className="space-y-6 min-w-0">
                  {/* Header */}
                  <div>
                    <h2 className="text-2xl font-bold mb-2">{t('quoteBuilder.step3.title')}</h2>
                    <p className="text-muted-foreground">{t('quoteBuilder.step3.subtitle')}</p>
                  </div>

                  {/* Trip Summary */}
                  <Card className="p-6 bg-gradient-to-br from-blue-50 to-purple-50">
                    <h3 className="text-lg font-semibold mb-4">{t('quoteBuilder.step3.tripSummary')}</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-sm text-muted-foreground">{t('quoteBuilder.step3.startDate')}</p>
                        <p className="font-semibold">{travelDate ? new Date(travelDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">{t('quoteBuilder.step3.travelers')}</p>
                        <p className="font-semibold">{t('quoteBuilder.step3.personCount', { count: globalTravelers })}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">{t('quoteBuilder.step3.days')}</p>
                        <p className="font-semibold">{t('quoteBuilder.step3.dayCount', { count: cityServices.length })}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">{t('quoteBuilder.step3.totalPrice')}</p>
                        <p className="font-semibold text-primary"><TourPrice amount={totalPricing?.totalAmount} /></p>
                      </div>
                    </div>
                  </Card>

                  {/* Day-by-Day Breakdown */}
                  <Accordion type="multiple" defaultValue={cityServices.map((c) => `review-day-${c.dayNumber}`)} className="space-y-4">
                    {cityServices.map((city, index) => {
                      const cityBreakdown = totalPricing?.breakdown?.find((b: any) => b.city === city.cityName);
                      
                      return (
                        <AccordionItem key={index} value={`review-day-${city.dayNumber}`} className="border rounded-lg">
                          <AccordionTrigger className="px-4 py-3 hover:no-underline">
                            <div className="flex items-center gap-4 w-full">
                              <Badge className="bg-primary">{t('quoteBuilder.step2.day', { number: city.dayNumber })}</Badge>
                              <div className="flex items-center gap-2">
                                <MapPin className="w-4 h-4 text-primary" />
                                <span className="font-semibold">{city.cityName}</span>
                              </div>
                              <div className="ml-auto mr-2">
                                <span className="text-sm font-semibold"><TourPrice amount={cityBreakdown?.total} /></span>
                              </div>
                            </div>
                          </AccordionTrigger>
                          
                          <AccordionContent className="px-4 pb-4 pt-2">
                            <div className="space-y-3">
                              {/* Catalog services (Phase C) */}
                              {city.selectedServices && city.selectedServices.length > 0 && (
                                <div>
                                  <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                                    <MapPinned className="w-4 h-4" />
                                    {t('quoteBuilder.step3.services')}
                                  </h4>
                                  <ul className="text-sm space-y-1 ml-6">
                                    {city.selectedServices.map((s) => (
                                      <li key={s.slug}>
                                        • {s.name ?? s.slug} —{" "}
                                        {vehicleLabel(t, s.vehicleSlug)}
                                        {" "}({tripTypeLabel(t, s.tripType)})
                                        {typeof s.price === "number" && (
                                          <span className="text-muted-foreground">
                                            {" "}— <TourPrice amount={s.price} />
                                          </span>
                                        )}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}

                              {/* Guide */}
                              {city.selectedGuide && (
                                <div>
                                  <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                                    <Users className="w-4 h-4" />
                                    {t('quoteBuilder.step3.tourGuide')}
                                  </h4>
                                  <p className="text-sm ml-6">• {t('quoteBuilder.step3.guideLine', { language: city.selectedGuide.language, hours: city.selectedGuide.duration })}</p>
                                </div>
                              )}

                              {/* Attractions */}
                              {city.selectedAttractions && city.selectedAttractions.length > 0 && (
                                <div>
                                  <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                                    <Star className="w-4 h-4" />
                                    {t('quoteBuilder.step3.attractions')}
                                  </h4>
                                  <ul className="text-sm space-y-1 ml-6">
                                    {city.selectedAttractions.map((attraction, i) => (
                                      <li key={i}>• {attraction}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}

                              {/* Add-ons */}
                              {city.selectedAddOns && city.selectedAddOns.length > 0 && (
                                <div>
                                  <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                                    <Package className="w-4 h-4" />
                                    {t('quoteBuilder.step3.addOns')}
                                  </h4>
                                  <ul className="text-sm space-y-1 ml-6">
                                    {city.selectedAddOns.map((addon, i) => (
                                      <li key={i}>• {addon.name} (x{addon.quantity})</li>
                                    ))}
                                  </ul>
                                </div>
                              )}

                              {/* Edit Button */}
                              <div className="pt-2 border-t">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => editDay(city.dayNumber)}
                                  className="text-primary hover:text-primary/80"
                                >
                                  {t('quoteBuilder.step3.editDay', { number: city.dayNumber })}
                                </Button>
                              </div>
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      );
                    })}
                  </Accordion>

                  {/* Navigation — the single checkout CTA for this step. The
                      price panel deliberately renders no button here so there
                      is exactly one "Proceed to Checkout" on screen. */}
                  <div className="pt-6 border-t">
                    <div className="flex flex-col-reverse sm:flex-row justify-center sm:justify-between gap-3">
                      <Button
                        variant="outline"
                        onClick={goToPreviousStep}
                        size="lg"
                        className="w-full sm:w-auto"
                      >
                        <ChevronLeft className="w-4 h-4 mr-2" />
                        {t('quoteBuilder.step3.back')}
                      </Button>
                      <Button
                        onClick={goToNextStep}
                        disabled={!(totalPricing && totalPricing.totalAmount > 0)}
                        size="lg"
                        className="bg-gradient-to-r from-primary to-blue-600 w-full sm:w-auto"
                      >
                        {t('quoteBuilder.step3.proceed')}
                        <ChevronRight className="w-4 h-4 ml-2" />
                      </Button>
                    </div>
                    {!(totalPricing && totalPricing.totalAmount > 0) && (
                      <p role="status" className="mt-3 text-sm text-red-600 text-center sm:text-right">
                        {t('quoteBuilder.step3.zeroTotal')}
                      </p>
                    )}
                    <FieldError id="step3-total-error" message={stepErrors.total} />
                  </div>
                  </div>
                  <LivePriceSummary totalPricing={totalPricing} />
                  </div>
                </div>
              )}

              {/* STEP 4: Checkout */}
              {currentStep === 4 && (
                <div className="animate-in fade-in duration-300">
                  <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
                  <div className="space-y-4 min-w-0">
                  {/* Header */}
                  <div className="text-center">
                    <h2 className="text-xl font-bold mb-1">{t('quoteBuilder.step4.title')}</h2>
                    <p className="text-sm text-muted-foreground">{t('quoteBuilder.step4.subtitle')}</p>
                  </div>

                  <div className="grid grid-cols-1 gap-4">
                    {/* Compact Form */}
                    <Card className="p-4">
                      <h3 className="font-semibold mb-3">{t('quoteBuilder.step4.contactInformation')}</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="md:col-span-2">
                          <Label htmlFor="checkout-name" className="text-sm">{t('quoteBuilder.step4.fullName')}</Label>
                          <Input
                            id="checkout-name"
                            placeholder={t('quoteBuilder.step4.namePlaceholder')}
                            required
                            autoComplete="name"
                            className="mt-1 h-9"
                            value={checkoutData.name}
                            onChange={(e) => setCheckoutData(prev => ({ ...prev, name: e.target.value }))}
                            data-testid="input-customer-name"
                          />
                        </div>
                        <div>
                          <Label htmlFor="checkout-email" className="text-sm">{t('quoteBuilder.step4.email')}</Label>
                          <Input
                            id="checkout-email"
                            type="email"
                            placeholder="john@example.com"
                            required
                            autoComplete="email"
                            className="mt-1 h-9"
                            value={checkoutData.email}
                            onChange={(e) => setCheckoutData(prev => ({ ...prev, email: e.target.value }))}
                            data-testid="input-customer-email"
                          />
                        </div>
                        <div>
                          <Label htmlFor="checkout-phone" className="text-sm">{t('quoteBuilder.step4.phone')}</Label>
                          <Input
                            id="checkout-phone"
                            type="tel"
                            placeholder="+1 234 567 8900"
                            required
                            autoComplete="tel"
                            className="mt-1 h-9"
                            value={checkoutData.phone}
                            onChange={(e) => setCheckoutData(prev => ({ ...prev, phone: e.target.value }))}
                            data-testid="input-customer-phone"
                          />
                        </div>
                        <div>
                          <Label htmlFor="checkout-nationality" className="text-sm">{t('quoteBuilder.step4.nationality')}</Label>
                          <Input
                            id="checkout-nationality"
                            placeholder={t('quoteBuilder.step4.nationalityPlaceholder')}
                            autoComplete="country-name"
                            className="mt-1 h-9"
                            value={checkoutData.nationality}
                            onChange={(e) => setCheckoutData(prev => ({ ...prev, nationality: e.target.value }))}
                            data-testid="input-customer-nationality"
                          />
                        </div>
                        <div>
                          <Label htmlFor="checkout-accommodation" className="text-sm">{t('quoteBuilder.step4.accommodation')}</Label>
                          <Input
                            id="checkout-accommodation"
                            placeholder={t('quoteBuilder.step4.accommodationPlaceholder')}
                            className="mt-1 h-9"
                            value={checkoutData.accommodation}
                            onChange={(e) => setCheckoutData(prev => ({ ...prev, accommodation: e.target.value }))}
                            data-testid="input-customer-accommodation"
                          />
                        </div>
                      </div>

                      {/* Special Requests - Compact */}
                      <div className="mt-3">
                        <Label htmlFor="checkout-requests" className="text-sm">{t('quoteBuilder.step4.specialRequests')}</Label>
                        <textarea
                          id="checkout-requests"
                          className="w-full mt-1 h-16 px-3 py-2 text-sm border rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-primary"
                          placeholder={t('quoteBuilder.step4.specialRequestsPlaceholder')}
                          value={checkoutData.specialRequests}
                          onChange={(e) => setCheckoutData(prev => ({ ...prev, specialRequests: e.target.value }))}
                          data-testid="input-special-requests"
                        />
                      </div>

                      {/* Terms & Conditions - Compact */}
                      <div className="mt-3 pt-3 border-t">
                        <h3 className="font-semibold mb-2">{t('quoteBuilder.step4.termsHeading')}</h3>
                        <div className="space-y-2">
                          <div className="flex items-start gap-2">
                            <Checkbox 
                              id="terms-service" 
                              checked={checkoutData.termsAccepted}
                              onCheckedChange={(checked) => setCheckoutData(prev => ({ ...prev, termsAccepted: checked as boolean }))}
                              data-testid="checkbox-terms"
                              className="mt-0.5"
                            />
                            <Label htmlFor="terms-service" className="text-xs leading-tight cursor-pointer">
                              <Trans
                                i18nKey="quoteBuilder.step4.agreeTerms"
                                components={{
                                  terms: <a href="/terms-of-service" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer" />,
                                  privacy: <a href="/privacy-policy" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer" />,
                                }}
                              />
                            </Label>
                          </div>
                          <div className="flex items-start gap-2">
                            <Checkbox 
                              id="booking-policy" 
                              checked={checkoutData.bookingPolicyAccepted}
                              onCheckedChange={(checked) => setCheckoutData(prev => ({ ...prev, bookingPolicyAccepted: checked as boolean }))}
                              data-testid="checkbox-booking-policy"
                              className="mt-0.5"
                            />
                            <Label htmlFor="booking-policy" className="text-xs leading-tight cursor-pointer">
                              <Trans
                                i18nKey="quoteBuilder.step4.agreeBookingPolicy"
                                components={{
                                  policy: <a href="/booking-agreement" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer" />,
                                }}
                              />
                            </Label>
                          </div>
                        </div>
                      </div>
                    </Card>

                    {/* Order Summary - Compact */}
                    <Card className="p-4">
                      <h3 className="font-semibold mb-3">{t('quoteBuilder.step4.orderSummary')}</h3>
                        
                      {/* Trip Details */}
                      <div className="grid grid-cols-3 gap-2 mb-3 pb-3 border-b">
                        <div className="text-center">
                          <p className="text-xs text-muted-foreground">{t('quoteBuilder.step4.travelDate')}</p>
                          <p className="text-sm font-medium">{travelDate ? new Date(travelDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '-'}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-muted-foreground">{t('quoteBuilder.step3.travelers')}</p>
                          <p className="text-sm font-medium">{t('quoteBuilder.step3.personCount', { count: globalTravelers })}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-muted-foreground">{t('quoteBuilder.step3.days')}</p>
                          <p className="text-sm font-medium">{t('quoteBuilder.step3.dayCount', { count: cityServices.length })}</p>
                        </div>
                      </div>

                      {/* Price Breakdown */}
                      <div className="space-y-1.5 mb-3 pb-3 border-b text-sm">
                        <div className="flex justify-between">
                          <span>{t('quoteBuilder.step4.priceServices')}</span>
                          <span><TourPrice amount={totalPricing?.breakdown?.reduce((sum: number, city: any) => sum + (city.routes || 0), 0)} /></span>
                        </div>
                        <div className="flex justify-between">
                          <span>{t('quoteBuilder.step4.priceGuides')}</span>
                          <span><TourPrice amount={totalPricing?.breakdown?.reduce((sum: number, city: any) => sum + (city.guide || 0), 0)} /></span>
                        </div>
                        <div className="flex justify-between">
                          <span>{t('quoteBuilder.step4.priceEntranceFees')}</span>
                          <span><TourPrice amount={totalPricing?.breakdown?.reduce((sum: number, city: any) => sum + (city.attractions || 0), 0)} /></span>
                        </div>
                        <div className="flex justify-between">
                          <span>{t('quoteBuilder.step4.priceAddOns')}</span>
                          <span><TourPrice amount={totalPricing?.breakdown?.reduce((sum: number, city: any) => sum + (city.addOns || 0), 0)} /></span>
                        </div>
                      </div>

                      {/* Total */}
                      <div className="flex justify-between items-center mb-4">
                        <span className="font-semibold">{t('quoteBuilder.step4.total')}</span>
                        <span className="text-xl font-bold text-primary"><TourPrice amount={totalPricing?.totalAmount} showNote /></span>
                      </div>

                      {/* Submit Button */}
                      <Button
                        className="w-full bg-gradient-to-r from-primary to-blue-600 h-11"
                        onClick={async () => {
                          // Validation
                          if (!checkoutData.name.trim()) {
                            alert(t('quoteBuilder.validation.name'));
                            return;
                          }
                          if (!checkoutData.email.trim() || !checkoutData.email.includes('@')) {
                            alert(t('quoteBuilder.validation.email'));
                            return;
                          }
                          if (!checkoutData.phone.trim()) {
                            alert(t('quoteBuilder.validation.phone'));
                            return;
                          }
                          if (!checkoutData.termsAccepted) {
                            alert(t('quoteBuilder.validation.terms'));
                            return;
                          }
                          if (!checkoutData.bookingPolicyAccepted) {
                            alert(t('quoteBuilder.validation.bookingPolicy'));
                            return;
                          }
                          
                          try {
                            // Create quote first
                            const quoteData = {
                              // Full itinerary priced + frozen server-side via
                              // buildMultiCityQuote (the same engine as the live
                              // preview), so the frozen/charged total matches
                              // what the customer saw. The server ignores any
                              // client total and recomputes from these.
                              cityServices,
                              travelers: globalTravelers,
                              jsonBlob: {
                                cityServices,
                                travelDate,
                                travelers: globalTravelers,
                                totalPricing
                              }
                            };

                            const quoteResponse = await fetch('/api/quotes', {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify(quoteData)
                            });

                            if (!quoteResponse.ok) throw new Error('Failed to create quote');
                            const quote = await quoteResponse.json();

                            // "Request quotes" goal in Google Ads, via the
                            // imported GA4 event `qualify_lead`. Marked so that
                            // opening /book/:id for this same quote later does
                            // not report a second lead.
                            if (quote?.id && !hasSentLead(quote.id)) {
                              trackQualifiedLead({
                                quoteId: quote.id,
                                value: totalPricing?.totalAmount,
                                currency: 'EGP',
                              });
                              markLeadSent(quote.id);
                            }

                            // Create booking with quote
                            const bookingData = {
                              quoteId: quote.id,
                              travelDate,
                              customerName: checkoutData.name,
                              customerEmail: checkoutData.email,
                              customerPhone: checkoutData.phone,
                              totalAmount: totalPricing?.totalAmount || 0,
                              specialRequests: checkoutData.specialRequests,
                              paymentMethod: 'pending',
                              paymentStatus: 'pending'
                            };

                            const bookingResponse = await fetch('/api/bookings', {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify(bookingData)
                            });

                            if (!bookingResponse.ok) throw new Error('Failed to create booking');
                            const booking = await bookingResponse.json();

                            // Report the purchase HERE, not only on
                            // booking-confirmation.tsx. This flow never
                            // navigates there — it alerts and resets to step 1
                            // — so relying on that page alone would miss every
                            // booking made through the main funnel, counting
                            // only the customers who later open the link in
                            // their email. markConversionSent keeps the
                            // confirmation page from reporting it a second time
                            // when they do.
                            if (booking?.bookingReference
                                && !hasSentConversion(booking.bookingReference)) {
                              const bookedValue = Number(totalPricing?.totalAmount);
                              trackPurchase({
                                transactionId: booking.bookingReference,
                                value: Number.isFinite(bookedValue) && bookedValue > 0
                                  ? bookedValue
                                  : undefined,
                                currency: 'EGP',
                                items: [{
                                  item_id: booking.bookingReference,
                                  item_name: 'Egypt trip booking', // i18n-exempt: GA4 item name, not UI
                                  item_category: 'trip',
                                  price: Number.isFinite(bookedValue) && bookedValue > 0
                                    ? bookedValue
                                    : undefined,
                                  quantity: 1,
                                }],
                              });
                              markConversionSent(booking.bookingReference);
                            }

                            alert(t('quoteBuilder.validation.submitted', {
                              email: checkoutData.email,
                              reference: booking.bookingReference,
                            }));
                            
                            // Reset form and go back to step 1
                            setCheckoutData({
                              name: '',
                              email: '',
                              phone: '',
                              nationality: '',
                              accommodation: '',
                              specialRequests: '',
                              termsAccepted: false,
                              bookingPolicyAccepted: false,
                              updatesConsent: false
                            });
                            setCurrentStep(1);
                          } catch (error) {
                            console.error('Booking error:', error); // i18n-exempt: console, not UI
                            alert(t('quoteBuilder.validation.submitFailed'));
                          }
                        }}
                        data-testid="button-submit-booking"
                      >
                        <CreditCard className="w-4 h-4 mr-2" />
                        {t('quoteBuilder.step4.requestBooking')}
                      </Button>

                      <p className="text-xs text-center text-muted-foreground mt-2">
                        {t('quoteBuilder.step4.disclaimer')}
                      </p>
                    </Card>
                  </div>

                  {/* Navigation */}
                  <div className="flex justify-center pt-4 border-t">
                    <Button
                      variant="outline"
                      onClick={goToPreviousStep}
                      className="w-full sm:w-auto"
                    >
                      <ChevronLeft className="w-4 h-4 mr-2" />
                      {t('quoteBuilder.step4.back')}
                    </Button>
                  </div>
                  </div>
                  <LivePriceSummary totalPricing={totalPricing} />
                  </div>
                </div>
              )}

              {/* Pricing Breakdown.
                  Gated on a non-zero total, not just on a destination being
                  picked: a full breakdown of LE 0 / LE 0 / LE 0 the moment you
                  choose a city reads as a broken calculator, and it is the
                  loudest thing on screen at exactly the point where the user
                  has not told us anything to price yet. */}
              {cityServices.length > 0 && totalPricing && totalPricing.breakdown && (
                totalPricing.totalAmount > 0 ? (
                <div className="mt-8">
                  <Separator className="mb-4" />
                  <h3 className="text-lg font-semibold mb-4">{t('quoteBuilder.breakdown.title')}</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {totalPricing.breakdown.map((city: any, index: number) => (
                      <Card key={index} className="p-4">
                        <h4 className="font-medium mb-2">{city.city}</h4>
                        <div className="space-y-1 text-sm">
                          <div className="flex justify-between">
                            <span>{t('quoteBuilder.breakdown.services')}</span>
                            <span className="font-mono"><TourPrice amount={city.routes} /></span>
                          </div>
                          <div className="flex justify-between">
                            <span>{t('quoteBuilder.breakdown.guide')}</span>
                            <span className="font-mono"><TourPrice amount={city.guide} /></span>
                          </div>
                          <div className="flex justify-between">
                            <span>{t('quoteBuilder.breakdown.entranceFees')}</span>
                            <span className="font-mono"><TourPrice amount={city.attractions} /></span>
                          </div>
                          <div className="flex justify-between">
                            <span>{t('quoteBuilder.breakdown.addOns')}</span>
                            <span className="font-mono"><TourPrice amount={city.addOns} /></span>
                          </div>
                          <Separator className="my-2" />
                          <div className="flex justify-between font-semibold">
                            <span>{t('quoteBuilder.breakdown.total')}</span>
                            <span className="font-mono"><TourPrice amount={city.total} /></span>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                </div>
                ) : (
                <div className="mt-8">
                  <Separator className="mb-4" />
                  <p className="text-sm text-muted-foreground">
                    {t('quoteBuilder.breakdown.empty')}
                  </p>
                </div>
                )
              )}
              {/* End Step Content Container */}
              </div>
        </CardContent>
      </Card>
        </TabsContent>

        {showSavedQuotesTab && (
          <TabsContent value="saved">
            <QuoteManager
              currentQuote={getCurrentQuoteData()}
              onLoadQuote={loadQuoteData}
            />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}